import '@js-joda/timezone';
import { DateTimeFormatter, LocalDateTime, ZoneId, ZonedDateTime } from '@js-joda/core';
import type { Instant } from '@js-joda/core';
import type { ProviderInstrumentConfig } from '@thinkalpha/locates-ws-client';

/** The backend runs the business day from midnight to midnight in US Eastern. */
const MARKET_ZONE = ZoneId.of('America/New_York');

/** What `<input type="datetime-local">` reads and writes. */
const LOCAL_INPUT_FORMAT = DateTimeFormatter.ofPattern("yyyy-MM-dd'T'HH:mm");

/** A stored entry as far as the window rules care. */
export type SuspensionTimes = Pick<ProviderInstrumentConfig, 'suspended' | 'timestamp' | 'suspendedUntil'>;

/** Why a chosen end time is outside the window the API accepts. */
export type SuspendedUntilProblem = 'unreadable' | 'not-later' | 'after-business-day';

/** What the end-time field currently holds. */
export type SuspendedUntilDraft =
    | { readonly kind: 'none' }
    | { readonly kind: 'ok'; readonly value: Instant }
    | { readonly kind: 'problem'; readonly problem: SuspendedUntilProblem };

/** Midnight (US Eastern) that started the business day containing `at`. */
export function businessDayStart(at: Instant): Instant {
    return ZonedDateTime.ofInstant(at, MARKET_ZONE).withHour(0).withMinute(0).withSecond(0).withNano(0).toInstant();
}

/** Midnight (US Eastern) that ends that business day; the latest end time the API accepts. */
export function businessDayEnd(at: Instant): Instant {
    return ZonedDateTime.ofInstant(at, MARKET_ZONE)
        .withHour(0)
        .withMinute(0)
        .withSecond(0)
        .withNano(0)
        .plusDays(1)
        .toInstant();
}

/**
 * Whether an entry still suspends its symbol. An entry recorded before the business day began no
 * longer applies, and an explicit end time that has passed ends the suspension. Without an explicit
 * end time the route's or provider's window decides, and the page cannot see those, so such an
 * entry is reported as still in effect.
 */
export function isSuspensionInEffect(entry: SuspensionTimes, now: Instant): boolean {
    if (!entry.suspended) return false;
    if (entry.timestamp.isBefore(businessDayStart(now))) return false;
    return entry.suspendedUntil === undefined || entry.suspendedUntil.isAfter(now);
}

/** Mirrors the API's bounds: strictly later than now, no later than the business-day-ending midnight. */
export function checkSuspendedUntil(value: Instant, now: Instant): SuspendedUntilProblem | null {
    if (!value.isAfter(now)) return 'not-later';
    if (value.isAfter(businessDayEnd(now))) return 'after-business-day';
    return null;
}

/** An instant as a `datetime-local` value in the browser's zone. */
export function toLocalInputValue(instant: Instant): string {
    return LocalDateTime.ofInstant(instant, ZoneId.SYSTEM).format(LOCAL_INPUT_FORMAT);
}

/** Reads the end-time field, taken in the browser's zone, against the API's bounds. */
export function readSuspendedUntilDraft(value: string, now: Instant): SuspendedUntilDraft {
    const trimmed = value.trim();
    if (trimmed === '') return { kind: 'none' };
    let parsed: Instant;
    try {
        parsed = LocalDateTime.parse(trimmed).atZone(ZoneId.SYSTEM).toInstant();
    } catch {
        return { kind: 'problem', problem: 'unreadable' };
    }
    const problem = checkSuspendedUntil(parsed, now);
    return problem === null ? { kind: 'ok', value: parsed } : { kind: 'problem', problem };
}

/**
 * Reads the field like {@link readSuspendedUntilDraft}, except that an untouched pre-fill of a stored
 * end time that is still ahead resolves to that exact instant, so seconds the minute-only field cannot
 * show survive a save and a hold ending later this minute is not reported as already past.
 */
export function resolveSuspendedUntilDraft(
    value: string,
    stored: Instant | undefined,
    now: Instant,
): SuspendedUntilDraft {
    if (stored !== undefined && stored.isAfter(now) && value === toLocalInputValue(stored)) {
        return { kind: 'ok', value: stored };
    }
    return readSuspendedUntilDraft(value, now);
}

/** The end time to pre-fill an edit with: the stored one while it is still ahead, otherwise blank. */
export function suspendedUntilDraftValue(entry: SuspensionTimes, now: Instant): string {
    if (entry.suspendedUntil === undefined || !entry.suspendedUntil.isAfter(now)) return '';
    return toLocalInputValue(entry.suspendedUntil);
}
